<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

// components
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// composables
import { useBranding } from 'shared/composables/useBranding';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';

// utils & constants
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { CAPTAIN_EVENTS } from '../../../../helper/AnalyticsHelper/events';

const props = defineProps({
  suggestedLabels: {
    type: Array,
    required: true,
  },
  chatLabels: {
    type: Array,
    required: false,
    default: () => [],
  },
});

defineOptions({
  name: 'LabelSuggestion',
});

const store = useStore();
const { t } = useI18n();

const { captainTasksEnabled } = useCaptain();
const { replaceInstallationName } = useBranding();

const isDismissed = ref(false);
const isHovered = ref(false);
const selectedLabels = ref([]);

const allLabels = computed(() => store.getters['labels/getLabels']);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const currentChat = computed(() => store.getters.getSelectedChat);

const conversationId = computed(() => currentChat.value?.id);

const preparedLabels = computed(() => {
  return allLabels.value.filter(label =>
    props.suggestedLabels.includes(label.title)
  );
});

const labelTooltip = computed(() => {
  if (preparedLabels.value.length > 1) {
    return t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.MULTIPLE_SUGGESTION');
  }

  return t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.SINGLE_SUGGESTION');
});

const addButtonText = computed(() => {
  if (selectedLabels.value.length === 1) {
    return t('LABEL_MGMT.SUGGESTIONS.ADD_SELECTED_LABEL');
  }

  if (selectedLabels.value.length > 1) {
    return t('LABEL_MGMT.SUGGESTIONS.ADD_SELECTED_LABELS');
  }

  return t('LABEL_MGMT.SUGGESTIONS.ADD_ALL_LABELS');
});

const shouldShowSuggestions = computed(() => {
  if (isDismissed.value) return false;
  if (!captainTasksEnabled.value) return false;

  return preparedLabels.value.length && props.chatLabels.length === 0;
});

const isConversationDismissed = () => {
  return LocalStorage.getFlag(
    LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
    currentAccountId.value,
    conversationId.value
  );
};

const trackLabelEvent = event => {
  const payload = {
    conversationId: conversationId.value,
    account: currentAccountId.value,
    suggestions: props.suggestedLabels,
    labelsApplied: selectedLabels.value.length
      ? selectedLabels.value
      : props.suggestedLabels,
  };

  useTrack(event, payload);
};

const dismissSuggestions = () => {
  LocalStorage.setFlag(
    LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
    currentAccountId.value,
    conversationId.value
  );

  isDismissed.value = true;
  trackLabelEvent(CAPTAIN_EVENTS.LABEL_SUGGESTION_DISMISSED);
};

const addAllLabels = () => {
  let labelsToAdd = selectedLabels.value;
  if (!labelsToAdd.length) {
    labelsToAdd = preparedLabels.value.map(label => label.title);
  }
  store.dispatch('conversationLabels/update', {
    conversationId: conversationId.value,
    labels: labelsToAdd,
  });
  trackLabelEvent(CAPTAIN_EVENTS.LABEL_SUGGESTION_APPLIED);
};

const pushOrAddLabel = label => {
  if (preparedLabels.value.length === 1) {
    addAllLabels();
    return;
  }

  if (!selectedLabels.value.includes(label)) {
    selectedLabels.value.push(label);
  } else {
    selectedLabels.value = selectedLabels.value.filter(l => l !== label);
  }
};

watch(
  conversationId,
  () => {
    selectedLabels.value = [];
    isDismissed.value = isConversationDismissed();
  },
  { immediate: true }
);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <li
    v-if="shouldShowSuggestions"
    class="flex flex-row justify-end mt-4 list-none"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="flex">
      <div class="max-w-[300px]">
        <h6 class="text-n-slate-11 mt-0.5 text-xxs">
          {{ $t('LABEL_MGMT.SUGGESTIONS.SUGGESTED_LABELS') }}
        </h6>
        <div class="gap-0.5 text-end flex items-center">
          <button
            v-for="label in preparedLabels"
            :key="label.title"
            v-tooltip.top="{
              content: selectedLabels.includes(label.title)
                ? $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.DESELECT')
                : labelTooltip,
              delay: { show: 600, hide: 0 },
              hideOnClick: true,
            }"
            class="!px-0 cursor-pointer"
            @click="pushOrAddLabel(label.title)"
          >
            <woot-label
              variant="dashed"
              v-bind="label"
              :bg-color="selectedLabels.includes(label.title) ? '#2781F6' : ''"
              class="cursor-pointer mb-0"
            />
          </button>
          <NextButton
            v-if="preparedLabels.length === 1"
            v-tooltip.top="{
              content: $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.DISMISS'),
              delay: { show: 600, hide: 0 },
              hideOnClick: true,
            }"
            faded
            xs
            icon="i-lucide-x"
            class="flex-shrink-0"
            :color="isHovered ? 'ruby' : 'blue'"
            @click="dismissSuggestions"
          />
        </div>
        <div
          v-if="preparedLabels.length > 1"
          class="inline-flex items-center gap-1"
        >
          <NextButton
            xs
            icon="i-lucide-plus"
            class="flex-shrink-0"
            :variant="selectedLabels.length === 0 ? 'faded' : 'solid'"
            :label="addButtonText"
            @click="addAllLabels"
          />
          <NextButton
            v-tooltip.top="{
              content: $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.DISMISS'),
              delay: { show: 600, hide: 0 },
              hideOnClick: true,
            }"
            faded
            xs
            icon="i-lucide-x"
            class="flex-shrink-0"
            :color="isHovered ? 'ruby' : 'blue'"
            @click="dismissSuggestions"
          />
        </div>
      </div>
      <div class="sender--info has-tooltip" data-original-title="null">
        <Avatar
          v-tooltip.top="{
            content: replaceInstallationName(
              $t('LABEL_MGMT.SUGGESTIONS.POWERED_BY')
            ),
            delay: { show: 600, hide: 0 },
            hideOnClick: true,
          }"
          :size="16"
          name="chatwoot-ai"
          icon-name="i-lucide-sparkles"
        />
      </div>
    </div>
  </li>
</template>
